import { strFromU8, unzipSync } from 'fflate';

/** A table as the board stores it: one heading row, then the cells. */
export interface SheetTable {
  cols: string[];
  rows: string[][];
}

export const SHEET_MAX_COLS = 8;
export const SHEET_MAX_ROWS = 300;
const MAX_CELL = 120;

/** "AB" -> 27: the column a cell reference points at, so gaps stay gaps. */
function columnIndex(ref: string): number {
  const letters = ref.replace(/[0-9]/g, '');
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return Math.max(0, n - 1);
}

function cell(value: string): string {
  const text = value.replace(/\s+/g, ' ').trim();
  return text.length > MAX_CELL ? `${text.slice(0, MAX_CELL - 1)}…` : text;
}

/**
 * Split one CSV line. Excel quotes any field holding the separator, and doubles
 * a quote inside a quoted field; nothing else needs handling here.
 */
function csvLine(line: string, sep: string): string[] {
  const out: string[] = [];
  let value = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        value += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else value += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === sep) {
      out.push(value);
      value = '';
    } else value += ch;
  }
  out.push(value);
  return out;
}

function fromCsv(text: string): string[][] {
  const body = text.replace(/^﻿/, '');
  const first = body.split(/\r?\n/, 1)[0] ?? '';
  // Arabic Windows exports are usually semicolon-separated; take whichever wins.
  const sep = (first.match(/;/g)?.length ?? 0) > (first.match(/,/g)?.length ?? 0) ? ';' : ',';
  return body
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0)
    .map((line) => csvLine(line, sep).map(cell));
}

/** The first worksheet named by the workbook, rather than assuming sheet1.xml. */
function firstSheetPath(files: Record<string, Uint8Array>): string {
  const rels = files['xl/_rels/workbook.xml.rels'];
  const book = files['xl/workbook.xml'];
  if (rels && book) {
    const parser = new DOMParser();
    const sheet = parser.parseFromString(strFromU8(book), 'application/xml').getElementsByTagName('sheet')[0];
    const id = sheet?.getAttribute('r:id') ?? sheet?.getAttribute('id');
    if (id) {
      for (const node of Array.from(
        parser.parseFromString(strFromU8(rels), 'application/xml').getElementsByTagName('Relationship'),
      )) {
        if (node.getAttribute('Id') === id) {
          const target = node.getAttribute('Target') ?? '';
          const path = target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`;
          if (files[path]) return path;
        }
      }
    }
  }
  return Object.keys(files).find((n) => /^xl\/worksheets\/sheet\d+\.xml$/.test(n)) ?? '';
}

function fromXlsx(bytes: Uint8Array): string[][] {
  const files = unzipSync(bytes);
  const path = firstSheetPath(files);
  if (!path) throw new Error('لم يُعثر على ورقة بيانات داخل الملف');

  const parser = new DOMParser();
  const shared: string[] = [];
  const sharedXml = files['xl/sharedStrings.xml'];
  if (sharedXml) {
    for (const si of Array.from(
      parser.parseFromString(strFromU8(sharedXml), 'application/xml').getElementsByTagName('si'),
    )) {
      // A run-formatted string is several <t> pieces of one value.
      shared.push(
        Array.from(si.getElementsByTagName('t'))
          .map((t) => t.textContent ?? '')
          .join(''),
      );
    }
  }

  const sheet = parser.parseFromString(strFromU8(files[path]!), 'application/xml');
  const out: string[][] = [];
  for (const row of Array.from(sheet.getElementsByTagName('row'))) {
    const cells: string[] = [];
    for (const c of Array.from(row.getElementsByTagName('c'))) {
      const at = columnIndex(c.getAttribute('r') ?? '');
      const type = c.getAttribute('t');
      let value = '';
      if (type === 's') {
        const index = Number(c.getElementsByTagName('v')[0]?.textContent ?? '-1');
        value = shared[index] ?? '';
      } else if (type === 'inlineStr') {
        value = Array.from(c.getElementsByTagName('t'))
          .map((t) => t.textContent ?? '')
          .join('');
      } else {
        value = c.getElementsByTagName('v')[0]?.textContent ?? '';
      }
      while (cells.length < at) cells.push('');
      cells[at] = cell(value);
    }
    out.push(cells);
  }
  return out;
}

/** Drop empty leading rows and trailing empty columns, and hold to the caps. */
function tidy(raw: string[][]): SheetTable {
  const rows = raw.filter((r) => r.some((c) => c !== ''));
  if (rows.length === 0) throw new Error('الملف فارغ');
  const width = Math.min(SHEET_MAX_COLS, Math.max(...rows.map((r) => r.length)));
  const square = rows.map((r) => Array.from({ length: width }, (_, i) => r[i] ?? ''));
  const [head, ...body] = square;
  return { cols: head!, rows: body.slice(0, SHEET_MAX_ROWS) };
}

/**
 * Read a table out of a spreadsheet the school already has. Excel's own .xlsx
 * is a zip of XML, which is unpacked here rather than on the server: the file
 * never leaves the browser, only the cells that end up on the board do.
 */
export async function readSheet(file: File): Promise<SheetTable> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv') || name.endsWith('.txt')) return tidy(fromCsv(await file.text()));
  if (name.endsWith('.xls')) {
    throw new Error('صيغة .xls القديمة غير مدعومة؛ احفظ الملف بصيغة .xlsx أو .csv من Excel');
  }
  return tidy(fromXlsx(new Uint8Array(await file.arrayBuffer())));
}
